/**
 * Interruptores de los avisos por correo (maqueta «Configuración de correos», parte de avisos).
 *
 * Cuatro tipos de aviso con su ícono en un círculo de color y, aparte, «Avisarme también de lo que hago
 * yo». Cada cambio se guarda al instante (PUT completo, optimista): «Guardado» un momento si salió bien;
 * si falla, el interruptor vuelve a como estaba y el error se dice junto al formulario.
 *
 * Lo usan «Mi cuenta» (`destino="yo"`) y el diálogo de Personas (`destino={id}`, solo ADMIN; la API
 * decide el permiso).
 */
import { useEffect, useId, useState, type ReactNode } from 'react';
import {
  ETIQUETA_AVISO_PROPIOS,
  ETIQUETA_TIPO_AVISO,
  TIPOS_AVISO,
  type TipoAviso,
} from '@cdtec-backlog/contrato';
import { EstadoError } from '../../componentes/EstadoSeccion';
import { Esqueleto, TextoCargando } from '../../componentes/Esqueleto';
import { Icono } from '../../componentes/Icono';
import { useMostrarTrasRetardo } from '../../componentes/useMostrarTrasRetardo';
import { mensajeDeError } from '../../lib/api';
import { fechaHora } from '../../lib/formato';
import type { DestinoCorreos } from './api';
import { useCorreos, useGuardarCorreos, type CambioAviso } from './hooks';
import './FormularioAvisos.css';

/** Cuánto se ve «Guardado» después de un cambio. */
export const DURACION_GUARDADO_MS = 2000;

export const DESCRIPCION_TIPO_AVISO: Record<TipoAviso, string> = {
  COMENTARIO: 'Alguien comenta una tarea que creaste o de la que eres responsable.',
  ASIGNADA: 'Quedas como responsable de una tarea, o dejas de serlo.',
  ESTADO: 'Una tarea tuya cambia de estado (por ejemplo, a En revisión o Hecha).',
  FECHAS:
    'Un correo de lunes a viernes a las 08:00 si tienes tareas atrasadas o que vencen pronto.',
};

export const DESCRIPCION_AVISO_PROPIOS = 'Útil para probar o para tener copia de lo que haces.';

/**
 * Íconos de línea de los correos (`public/correo/icono-*.svg`), con los mismos trazos pero en
 * `currentColor`: así siguen el tema (claro, oscuro y paletas) sin pedir imágenes.
 */
const TRAZOS: Record<TipoAviso | 'propios', ReactNode> = {
  COMENTARIO: (
    <>
      <path d="M4 5h16v11H9l-5 4z" />
      <path d="M8 9.5h8M8 12.5h5" />
    </>
  ),
  ASIGNADA: (
    <>
      <circle cx="10" cy="8" r="3.5" />
      <path d="M3.5 19.5c.8-3.4 3.4-5.2 6.5-5.2 1.2 0 2.3.3 3.2.8" />
      <path d="M18 14v6M15 17h6" />
    </>
  ),
  ESTADO: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M8 12.3l2.7 2.7L16.2 9.5" />
    </>
  ),
  FECHAS: (
    <>
      <rect x="4" y="5.5" width="16" height="14.5" rx="2" />
      <path d="M4 10h16M8.5 3.5v4M15.5 3.5v4M12 13v3M12 18v.1" />
    </>
  ),
  // Una flecha que vuelve a quien la lanzó: el aviso de lo que hiciste tú.
  propios: (
    <>
      <path d="M9 14l-4-4 4-4" />
      <path d="M5 10h9a5 5 0 0 1 0 10h-3" />
    </>
  ),
};

function IconoAviso({ tipo }: { tipo: TipoAviso | 'propios' }) {
  return (
    <span className={`avisos-correo__icono avisos-correo__icono--${tipo.toLowerCase()}`}>
      <svg
        viewBox="0 0 24 24"
        width="20"
        height="20"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        focusable="false"
      >
        {TRAZOS[tipo]}
      </svg>
    </span>
  );
}

function FilaAviso({
  id,
  tipo,
  titulo,
  descripcion,
  encendido,
  onCambiar,
}: {
  id: string;
  tipo: TipoAviso | 'propios';
  titulo: string;
  descripcion: string;
  encendido: boolean;
  onCambiar: (encendido: boolean) => void;
}) {
  return (
    <div className="avisos-correo__fila">
      <IconoAviso tipo={tipo} />
      <div className="avisos-correo__textos">
        <label htmlFor={id} className="avisos-correo__titulo">
          {titulo}
        </label>
        <span id={`${id}-desc`} className="avisos-correo__desc">
          {descripcion}
        </span>
      </div>
      <span className="interruptor">
        <input
          id={id}
          type="checkbox"
          role="switch"
          checked={encendido}
          aria-describedby={`${id}-desc`}
          onChange={(e) => onCambiar(e.target.checked)}
        />
        <span className="interruptor__riel" aria-hidden="true" />
      </span>
    </div>
  );
}

function FormularioCargando() {
  const visible = useMostrarTrasRetardo(true);
  return (
    <div className={`avisos-correo__lista${visible ? '' : ' carga--espera'}`} aria-busy="true">
      {TIPOS_AVISO.map((tipo) => (
        <div key={tipo} className="avisos-correo__fila">
          <Esqueleto forma="icono" />
          <div className="avisos-correo__textos">
            <Esqueleto forma="linea" ancho={40} />
            <Esqueleto forma="linea" ancho={80} />
          </div>
          <Esqueleto forma="chip" />
        </div>
      ))}
    </div>
  );
}

export function FormularioAvisos({
  destino,
  nombre,
  yoId,
  plano = false,
}: {
  destino: DestinoCorreos;
  /** Nombre de la persona cuando un ADMIN edita las de otra (cambia la descripción). */
  nombre?: string;
  /** Id de quien usa la web: si se edita a sí misma, se refresca también la otra vista. */
  yoId?: number;
  /** Sin caja propia (dentro de un diálogo, que ya tiene la suya). */
  plano?: boolean;
}) {
  const base = useId();
  const correos = useCorreos(destino);
  const guardar = useGuardarCorreos(destino, yoId);
  // Instante del último guardado que salió bien: cada uno reinicia los 2 s de «Guardado».
  const [guardadoEn, setGuardadoEn] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (guardadoEn === null) return;
    const reloj = window.setTimeout(() => setGuardadoEn(null), DURACION_GUARDADO_MS);
    return () => window.clearTimeout(reloj);
  }, [guardadoEn]);

  /**
   * Cada cambio tiene su propia promesa (`mutateAsync`): los callbacks de `mutate` solo avisan del último, y con dos
   * cambios seguidos el primero quedaría sin «Guardado» o sin error.
   */
  const cambiar = (cambio: CambioAviso) => {
    setError(null);
    const titulo =
      cambio.clave === 'propios' ? ETIQUETA_AVISO_PROPIOS : ETIQUETA_TIPO_AVISO[cambio.clave];
    void guardar.mutateAsync(cambio).then(
      () => setGuardadoEn(Date.now()),
      (e: unknown) => {
        setGuardadoEn(null);
        setError(
          `No se pudo guardar «${titulo}»: ${mensajeDeError(e)} Se muestra lo que quedó guardado.`,
        );
      },
    );
  };

  const descripcion = nombre
    ? `Avisos que recibe ${nombre}. Nunca se le avisa de lo que hizo, salvo que active la última opción.`
    : 'Elige de qué te avisamos. Nunca te avisamos de lo que hiciste tú, salvo que actives la última opción.';

  let contenido: ReactNode;
  if (correos.isPending) {
    contenido = (
      <>
        <TextoCargando>
          {nombre ? `Cargando los avisos de ${nombre}…` : 'Cargando tus avisos…'}
        </TextoCargando>
        <FormularioCargando />
      </>
    );
  } else if (correos.isError) {
    contenido = (
      <EstadoError
        texto={mensajeDeError(correos.error)}
        onReintentar={() => void correos.refetch()}
        reintentando={correos.isFetching}
      />
    );
  } else {
    const avisos = correos.data.avisos;
    contenido = (
      <>
        <div className="avisos-correo__lista">
          {TIPOS_AVISO.map((tipo) => (
            <FilaAviso
              key={tipo}
              id={`${base}-${tipo}`}
              tipo={tipo}
              titulo={ETIQUETA_TIPO_AVISO[tipo]}
              descripcion={DESCRIPCION_TIPO_AVISO[tipo]}
              encendido={avisos[tipo]}
              onCambiar={(valor) => cambiar({ clave: tipo, valor })}
            />
          ))}
        </div>
        <div className="avisos-correo__lista avisos-correo__lista--aparte">
          <FilaAviso
            id={`${base}-propios`}
            tipo="propios"
            titulo={ETIQUETA_AVISO_PROPIOS}
            descripcion={DESCRIPCION_AVISO_PROPIOS}
            encendido={avisos.propios}
            onCambiar={(valor) => cambiar({ clave: 'propios', valor })}
          />
        </div>
        {correos.data.modificadoPorAdmin && (
          <p className="avisos-correo__admin">
            Lo cambió un administrador
            {correos.data.actualizadoEn ? ` el ${fechaHora(correos.data.actualizadoEn)}` : ''}.
          </p>
        )}
      </>
    );
  }

  return (
    <div
      className={plano ? 'avisos-correo avisos-correo--plano' : 'avisos-correo'}
      aria-labelledby={`${base}-titulo`}
      role="group"
    >
      <div className="avisos-correo__cabecera">
        <h3 id={`${base}-titulo`} className="avisos-correo__titulo-seccion">
          Avisos por correo
        </h3>
        {/* Siempre en el DOM para que el lector anuncie el cambio. */}
        <span className="avisos-correo__estado" role="status">
          {guardadoEn !== null && (
            <>
              <Icono nombre="check" tamano={14} />
              Guardado
            </>
          )}
        </span>
      </div>
      <p className="avisos-correo__ayuda">
        {descripcion} Los correos llegan desde noreply@cdtec.cl.
      </p>
      {error && (
        <p className="avisos-correo__error" role="alert">
          {error}
        </p>
      )}
      {contenido}
    </div>
  );
}
